import axios from "axios"
import queryString from "query-string"
import lodash from "lodash"
import * as appDataService from "@/AppDataService"
import * as utils from "@/Shared/utils"

import {
  API_RULE,
  API_RULE_SET,
  API_GET_RULE_SET,
  API_XPATH_MAPPING,
  API_RULE_COLLECTION,
  API_GET_RULE_COLLECTION,
  API_TEST_VALIDATION_RULE,
  API_TEST_VALIDATION_RULE_SET,
  API_TEST_VALIDATION_RULE_COLLECTION,
  API_TEST_TRANSFORMATION_RULE,
  API_TEST_TRANSFORMATION_RULE_SET,
  API_TEST_TRANSFORMATION_RULE_COLLECTION,
  API_PROVIDER_CONFIGURATION,
  API_TEST_TRANSFORMATION_JS_RULE
} from "./urls"

/***********************
 * Provider Data Service
 ***********************/

/**
 * Retrieve provider groups
 * @param {*} retrieveSecondaryOwners
 */
export function retrieveSecondaryOwners(queryObjectInput, serviceURL) {
  return appDataService
    .getMPXUserInfo()
    .then(userInfo => {
      const token = userInfo.token
      let queryObject = {}
      if (!lodash.isUndefined(queryObjectInput)) {
        queryObject = queryObjectInput
      }
      queryObject["sort"] = "title"
      queryObject["token"] = token
      queryObject["serviceURL"] = serviceURL
      let providersURL = utils.getServerUrl() + API_PROVIDER_CONFIGURATION
      providersURL += "?" + queryString.stringify(queryObject)
      let secondaryOwners = []
      return axios
        .get(providersURL)
        .then(response => {
          let hasEntries = lodash.has(response, ["data", "entries"])
          if (hasEntries) {
            secondaryOwners = response.data.entries
          }
          // Special secondary owner 'Any' for rule management
          secondaryOwners.unshift({ title: "Any" })
          return secondaryOwners
        })
        .catch(error => {
          secondaryOwners = []
          return Promise.reject(error)
        })
    })
    .catch(error => {
      return Promise.reject(error)
    })
}

/*******************
 * Rule Data Service
 *******************/

/**
 * getRule - Retrieve rules
 * @param {object} queryObjectInput
 * @param {string} serviceURL
 * @returns {Promise}
 */
export function getRule(queryObjectInput, serviceURL) {
  return appDataService
    .getMPXUserInfo()
    .then(userInfo => {
      const token = userInfo.token
      let queryObject = {}
      if (!lodash.isUndefined(queryObjectInput)) {
        queryObject = queryObjectInput
      }
      queryObject["token"] = token
      queryObject["serviceURL"] = serviceURL
      let rulesURL = utils.getServerUrl() + API_RULE
      rulesURL += "?" + queryString.stringify(queryObject)
      let ruleResponse = {}
      return axios
        .get(rulesURL)
        .then(response => {
          ruleResponse = response
          return ruleResponse
        })
        .catch(error => {
          ruleResponse = {}
          return Promise.reject(error)
        })
    })
    .catch(error => {
      return Promise.reject(error)
    })
}

/**
 * createRule - Create Rule
 * @param {object} data
 * @param {string} accountId
 * @param {string} serviceURL
 * @returns {Promise}
 */
export function createRule(data, accountId, serviceURL) {
  return appDataService
    .getMPXUserInfo()
    .then(userInfo => {
      const token = userInfo.token
      let queryObject = {}
      queryObject["token"] = token
      queryObject["account"] = accountId
      queryObject["serviceURL"] = serviceURL
      let ruleURL = utils.getServerUrl() + API_RULE
      ruleURL += "?" + queryString.stringify(queryObject)
      let ruleResponse = {}
      return axios
        .post(ruleURL, data)
        .then(response => {
          ruleResponse = response
          return ruleResponse
        })
        .catch(error => {
          ruleResponse = {}
          return Promise.reject(error)
        })
    })
    .catch(error => {
      return Promise.reject(error)
    })
}

/**
 * updateRule - Update Rule
 * @param {object} data
 * @param {string} accountId
 * @param {string} serviceURL
 * @returns {Promise}
 */
export function updateRule(data, accountId, serviceURL) {
  return appDataService
    .getMPXUserInfo()
    .then(userInfo => {
      const token = userInfo.token
      let queryObject = {}
      queryObject["token"] = token
      queryObject["account"] = accountId
      queryObject["serviceURL"] = serviceURL
      let ruleURL = utils.getServerUrl() + API_RULE
      ruleURL += "?" + queryString.stringify(queryObject)
      let ruleResponse = {}
      return axios
        .put(ruleURL, data)
        .then(response => {
          ruleResponse = response
          return ruleResponse
        })
        .catch(error => {
          ruleResponse = {}
          return Promise.reject(error)
        })
    })
    .catch(error => {
      return Promise.reject(error)
    })
}

/**
 * deleteRule - Delete Rule
 * @param {string} ruleId
 * @param {string} accountId
 * @param {string} serviceURL
 * @returns {Promise}
 */
export function deleteRule(ruleId, serviceURL) {
  return appDataService
    .getMPXUserInfo()
    .then(userInfo => {
      const token = userInfo.token
      let queryObject = {}
      queryObject["token"] = token
      queryObject["ruleId"] = ruleId
      queryObject["serviceURL"] = serviceURL
      let ruleURL = utils.getServerUrl() + API_RULE
      ruleURL += "?" + queryString.stringify(queryObject)
      let ruleResponse = {}
      return axios
        .delete(ruleURL)
        .then(response => {
          ruleResponse = response
          return ruleResponse
        })
        .catch(error => {
          ruleResponse = {}
          return Promise.reject(error)
        })
    })
    .catch(error => {
      return Promise.reject(error)
    })
}

/***********************
 * Rule Set Data Service
 ***********************/
/**
 * getRuleSet - Retrieve rule sets
 * @param {object} queryObjectInput
 * @returns {Promise}
 */
export function getRuleSet(queryObjectInput, serviceURL) {
  return appDataService
    .getMPXUserInfo()
    .then(userInfo => {
      const token = userInfo.token
      let queryObject = {}
      if (!lodash.isUndefined(queryObjectInput)) {
        queryObject = queryObjectInput
      }
      queryObject["token"] = token
      queryObject["serviceURL"] = serviceURL
      let ruleSetURL = utils.getServerUrl() + API_RULE_SET
      ruleSetURL += "?" + queryString.stringify(queryObject)
      let ruleSetResponse = {}
      return axios
        .get(ruleSetURL)
        .then(response => {
          ruleSetResponse = response
          return ruleSetResponse
        })
        .catch(error => {
          ruleSetResponse = {}
          return Promise.reject(error)
        })
    })
    .catch(error => {
      return Promise.reject(error)
    })
}

/**
 * createRuleSet - Create RuleSet
 * @param {object} data
 * @param {string} accountId
 * @returns {Promise}
 */
export function createRuleSet(data, accountId, serviceURL) {
  return appDataService
    .getMPXUserInfo()
    .then(userInfo => {
      const token = userInfo.token
      let queryObject = {}
      queryObject["token"] = token
      queryObject["account"] = accountId
      queryObject["serviceURL"] = serviceURL
      let ruleURL = utils.getServerUrl() + API_RULE_SET
      ruleURL += "?" + queryString.stringify(queryObject)
      let ruleSetResponse = {}
      return axios
        .post(ruleURL, data)
        .then(response => {
          ruleSetResponse = response
          return ruleSetResponse
        })
        .catch(error => {
          ruleSetResponse = {}
          return Promise.reject(error)
        })
    })
    .catch(error => {
      return Promise.reject(error)
    })
}

/**
 * updateRuleSet - Update Rule
 * @param {object} data
 * @param {string} accountId
 * @returns {Promise}
 */
export function updateRuleSet(data, accountId, serviceURL) {
  return appDataService
    .getMPXUserInfo()
    .then(userInfo => {
      const token = userInfo.token
      let queryObject = {}
      queryObject["token"] = token
      queryObject["account"] = accountId
      queryObject["serviceURL"] = serviceURL
      let ruleSetURL = utils.getServerUrl() + API_RULE_SET
      ruleSetURL += "?" + queryString.stringify(queryObject)
      let ruleSetResponse = {}
      return axios
        .put(ruleSetURL, data)
        .then(response => {
          ruleSetResponse = response
          return ruleSetResponse
        })
        .catch(error => {
          ruleSetResponse = {}
          return Promise.reject(error)
        })
    })
    .catch(error => {
      return Promise.reject(error)
    })
}

/**
 * deleteRuleSet - Delete RuleSet
 * @param {object} ruleSetId
 * @param {string} accountId
 * @returns {Promise}
 */
export function deleteRuleSet(ruleSetId, serviceURL) {
  return appDataService
    .getMPXUserInfo()
    .then(userInfo => {
      const token = userInfo.token
      let queryObject = {}
      queryObject["token"] = token
      queryObject["ruleSetId"] = ruleSetId
      queryObject["serviceURL"] = serviceURL
      let ruleURL = utils.getServerUrl() + API_RULE_SET
      ruleURL += "?" + queryString.stringify(queryObject)
      let ruleSetResponse = {}
      return axios
        .delete(ruleURL)
        .then(response => {
          ruleSetResponse = response
          return ruleSetResponse
        })
        .catch(error => {
          ruleSetResponse = {}
          return Promise.reject(error)
        })
    })
    .catch(error => {
      return Promise.reject(error)
    })
}

/******************************
 * Rule Collection Data Service
 ******************************/
/**
 * getRuleCollection - Retrieve rule sets
 * @param {object} queryObjectInput
 * @returns {Promise}
 */
export function getRuleCollection(queryObjectInput, serviceURL) {
  return appDataService
    .getMPXUserInfo()
    .then(userInfo => {
      const token = userInfo.token
      let queryObject = {}
      if (!lodash.isUndefined(queryObjectInput)) {
        queryObject = queryObjectInput
      }
      queryObject["token"] = token
      queryObject["serviceURL"] = serviceURL
      let rulesURL = utils.getServerUrl() + API_RULE_COLLECTION
      rulesURL += "?" + queryString.stringify(queryObject)
      let ruleCollectionResponse = {}
      return axios
        .get(rulesURL)
        .then(response => {
          ruleCollectionResponse = response
          return ruleCollectionResponse
        })
        .catch(error => {
          ruleCollectionResponse = {}
          return Promise.reject(error)
        })
    })
    .catch(error => {
      return Promise.reject(error)
    })
}

/**
 * createRuleCollection - Create RuleCollection
 * @param {object} data
 * @param {string} accountId
 * @returns {Promise}
 */
export function createRuleCollection(data, accountId, serviceURL) {
  return appDataService
    .getMPXUserInfo()
    .then(userInfo => {
      const token = userInfo.token
      let queryObject = {}
      queryObject["token"] = token
      queryObject["account"] = accountId
      queryObject["serviceURL"] = serviceURL
      let ruleURL = utils.getServerUrl() + API_RULE_COLLECTION
      ruleURL += "?" + queryString.stringify(queryObject)
      let ruleCollectionResponse = {}
      return axios
        .post(ruleURL, data)
        .then(response => {
          ruleCollectionResponse = response
          return ruleCollectionResponse
        })
        .catch(error => {
          ruleCollectionResponse = {}
          return Promise.reject(error)
        })
    })
    .catch(error => {
      return Promise.reject(error)
    })
}

/**
 * updateRuleCollection - Update Rule
 * @param {*} data
 * @param {string} accountId
 * @returns {Promise}
 */
export function updateRuleCollection(data, accountId, serviceURL) {
  return appDataService
    .getMPXUserInfo()
    .then(userInfo => {
      const token = userInfo.token
      let queryObject = {}
      queryObject["token"] = token
      queryObject["account"] = accountId
      queryObject["serviceURL"] = serviceURL
      let ruleSetURL = utils.getServerUrl() + API_RULE_COLLECTION
      ruleSetURL += "?" + queryString.stringify(queryObject)
      let ruleCollectionResponse = {}
      return axios
        .put(ruleSetURL, data)
        .then(response => {
          ruleCollectionResponse = response
          return ruleCollectionResponse
        })
        .catch(error => {
          ruleCollectionResponse = {}
          return Promise.reject(error)
        })
    })
    .catch(error => {
      return Promise.reject(error)
    })
}

/**
 * deleteRuleCollection - Delete RuleSet
 * @param {*} ruleCollectionId
 * @param {string} accountId
 * @returns {Promise}
 */
export function deleteRuleCollection(ruleCollectionId, serviceURL) {
  return appDataService
    .getMPXUserInfo()
    .then(userInfo => {
      const token = userInfo.token
      let queryObject = {}
      queryObject["token"] = token
      queryObject["ruleCollectionId"] = ruleCollectionId
      queryObject["serviceURL"] = serviceURL
      let ruleURL = utils.getServerUrl() + API_RULE_COLLECTION
      ruleURL += "?" + queryString.stringify(queryObject)
      let ruleCollectionResponse = {}
      return axios
        .delete(ruleURL)
        .then(response => {
          ruleCollectionResponse = response
          return ruleCollectionResponse
        })
        .catch(error => {
          ruleCollectionResponse = {}
          return Promise.reject(error)
        })
    })
    .catch(error => {
      return Promise.reject(error)
    })
}

/****************************
 * Xpath Mapping Data Service
 ****************************/
/**
 * Retrieve templates
 * @param {*} queryObjectInput
 * @param {string} serviceURL
 * @returns {Promise}
 */
export function getXPathMapping(queryObjectInput, serviceURL) {
  return appDataService
    .getMPXUserInfo()
    .then(userInfo => {
      return userInfo.token
    })
    .then(token => {
      let queryObject = {}
      if (!lodash.isUndefined(queryObjectInput)) {
        queryObject = queryObjectInput
      }
      queryObject["token"] = token
      queryObject["serviceURL"] = serviceURL
      let xpathMappingURL = utils.getServerUrl() + API_XPATH_MAPPING
      xpathMappingURL += "?" + queryString.stringify(queryObject)
      let xpathMappingResponse = {}
      return axios
        .get(xpathMappingURL)
        .then(response => {
          xpathMappingResponse = response
          return xpathMappingResponse
        })
        .catch(error => {
          xpathMappingResponse = {}
          return Promise.reject(error)
        })
    })
}

/**
 * Add XPath Mapping
 * @param {*} queryObjectInput
 * @param {string} serviceURL
 * @returns {Promise}
 */
export function createXPathMapping(data, accountId, serviceURL) {
  return appDataService
    .getMPXUserInfo()
    .then(userInfo => {
      return userInfo.token
    })
    .then(token => {
      let queryObject = {}
      queryObject["token"] = token
      queryObject["account"] = accountId
      queryObject["serviceURL"] = serviceURL
      let xpathMappingURL = utils.getServerUrl() + API_XPATH_MAPPING
      xpathMappingURL += "?" + queryString.stringify(queryObject)
      let xpathMappingResponse = {}
      return axios
        .post(xpathMappingURL, data)
        .then(response => {
          xpathMappingResponse = response
          return xpathMappingResponse
        })
        .catch(error => {
          xpathMappingResponse = {}
          return Promise.reject(error)
        })
    })
}

/**
 * Update Mapping
 * @param {*} data
 * @param {string} serviceURL
 * @returns {Promise}
 */
export function updateXPathMapping(data, accountId, serviceURL) {
  return appDataService
    .getMPXUserInfo()
    .then(userInfo => {
      return userInfo.token
    })
    .then(token => {
      let queryObject = {}
      queryObject["token"] = token
      queryObject["serviceURL"] = serviceURL
      queryObject["account"] = accountId
      let xpathMappingURL = utils.getServerUrl() + API_XPATH_MAPPING
      xpathMappingURL += "?" + queryString.stringify(queryObject)
      let xpathMappingResponse = {}
      return axios
        .put(xpathMappingURL, data)
        .then(response => {
          xpathMappingResponse = response
          return xpathMappingResponse
        })
        .catch(error => {
          xpathMappingResponse = {}
          return Promise.reject(error)
        })
    })
}

/**
 * Add XPath Mapping
 * @param {*} queryObjectInput
 * @param {string} serviceURL
 * @returns {Promise}
 */
export function deleteXPathMapping(xpathMappingId, serviceURL) {
  return appDataService
    .getMPXUserInfo()
    .then(userInfo => {
      return userInfo.token
    })
    .then(token => {
      let queryObject = {}
      queryObject["token"] = token
      queryObject["xpathMappingId"] = xpathMappingId
      queryObject["serviceURL"] = serviceURL
      let xpathMappingURL = utils.getServerUrl() + API_XPATH_MAPPING
      xpathMappingURL += "?" + queryString.stringify(queryObject)
      let xpathMappingResponse = {}
      return axios
        .delete(xpathMappingURL)
        .then(response => {
          xpathMappingResponse = response
          return xpathMappingResponse
        })
        .catch(error => {
          xpathMappingResponse = {}
          return Promise.reject(error)
        })
    })
}

/*************
 * Web Service
 *************/

/**
 * getRuleSetList - Retrieve rule sets including
 * rules
 * @param {object} queryObjectInput
 * @returns {Promise}
 */
export function getRuleSetDetails(queryObjectInput, serviceURL) {
  return appDataService
    .getMPXUserInfo()
    .then(userInfo => {
      const token = userInfo.token
      let queryObject = {}
      if (!lodash.isUndefined(queryObjectInput)) {
        queryObject = queryObjectInput
      }
      queryObject["token"] = token
      queryObject["serviceURL"] = serviceURL
      let rulesURL = utils.getServerUrl() + API_GET_RULE_SET
      rulesURL += "?" + queryString.stringify(queryObject)
      let ruleResponse = {}
      return axios
        .get(rulesURL)
        .then(response => {
          ruleResponse = response
          return ruleResponse
        })
        .catch(error => {
          ruleResponse = {}
          return Promise.reject(error)
        })
    })
    .catch(error => {
      return Promise.reject(error)
    })
}

/**
 * getRuleCollectionDetails - Retrieve rule collection including
 * rules and rule sets
 * @param {object} queryObjectInput
 * @returns {Promise}
 */
export function getRuleCollectionDetails(queryObjectInput, serviceURL) {
  return appDataService
    .getMPXUserInfo()
    .then(userInfo => {
      const token = userInfo.token
      let queryObject = {}
      if (!lodash.isUndefined(queryObjectInput)) {
        queryObject = queryObjectInput
      }
      queryObject["token"] = token
      queryObject["serviceURL"] = serviceURL
      let rulesURL = utils.getServerUrl() + API_GET_RULE_COLLECTION
      rulesURL += "?" + queryString.stringify(queryObject)
      let ruleResponse = {}
      return axios
        .get(rulesURL)
        .then(response => {
          ruleResponse = response
          return ruleResponse
        })
        .catch(error => {
          ruleResponse = {}
          return Promise.reject(error)
        })
    })
    .catch(error => {
      return Promise.reject(error)
    })
}

/**
 * testValidationRule - test validation rule
 * @param {object} data
 * @returns {Promise}
 */
export function testValidationRule(data, accountId, serviceURL) {
  return appDataService
    .getMPXUserInfo()
    .then(userInfo => {
      const token = userInfo.token
      let queryObject = {}
      queryObject["token"] = token
      queryObject["serviceURL"] = serviceURL
      queryObject["account"] = accountId
      let testRuleURL = utils.getServerUrl() + API_TEST_VALIDATION_RULE
      testRuleURL += "?" + queryString.stringify(queryObject)
      let testRuleResponse = {}
      return axios
        .post(testRuleURL, data)
        .then(response => {
          testRuleResponse = response
          return testRuleResponse
        })
        .catch(error => {
          testRuleResponse = {}
          return Promise.reject(error)
        })
    })
    .catch(error => {
      return Promise.reject(error)
    })
}

/**
 * testValidationRuleSet - test validation rule set
 * @param {object} data
 * @returns {Promise}
 */
export function testValidationRuleSet(data, accountId, serviceURL) {
  return appDataService
    .getMPXUserInfo()
    .then(userInfo => {
      const token = userInfo.token
      let queryObject = {}
      queryObject["token"] = token
      queryObject["serviceURL"] = serviceURL
      queryObject["account"] = accountId
      let testRuleURL = utils.getServerUrl() + API_TEST_VALIDATION_RULE_SET
      testRuleURL += "?" + queryString.stringify(queryObject)
      let testRuleResponse = {}
      return axios
        .post(testRuleURL, data)
        .then(response => {
          testRuleResponse = response
          return testRuleResponse
        })
        .catch(error => {
          testRuleResponse = {}
          return Promise.reject(error)
        })
    })
    .catch(error => {
      return Promise.reject(error)
    })
}

/**
 * testValidationRuleCollection - test validation rule
 * @param {object} data
 * @returns {Promise}
 */
export function testValidationRuleCollection(data, accountId, serviceURL) {
  return appDataService
    .getMPXUserInfo()
    .then(userInfo => {
      const token = userInfo.token
      let queryObject = {}
      queryObject["token"] = token
      queryObject["serviceURL"] = serviceURL
      queryObject["account"] = accountId
      let testRuleURL =
        utils.getServerUrl() + API_TEST_VALIDATION_RULE_COLLECTION
      testRuleURL += "?" + queryString.stringify(queryObject)
      let testRuleResponse = {}
      return axios
        .post(testRuleURL, data)
        .then(response => {
          testRuleResponse = response
          return testRuleResponse
        })
        .catch(error => {
          testRuleResponse = {}
          return Promise.reject(error)
        })
    })
    .catch(error => {
      return Promise.reject(error)
    })
}

/**
 * testTransformationRule - test transformation rule
 * @param {object} data
 * @returns {Promise}
 */
export function testTransformationRule(data, accountId, serviceURL) {
  return appDataService
    .getMPXUserInfo()
    .then(userInfo => {
      const token = userInfo.token
      let queryObject = {}
      queryObject["token"] = token
      queryObject["serviceURL"] = serviceURL
      queryObject["account"] = accountId
      let testRuleURL = utils.getServerUrl() + API_TEST_TRANSFORMATION_RULE
      testRuleURL += "?" + queryString.stringify(queryObject)
      let testRuleResponse = {}
      return axios
        .post(testRuleURL, data)
        .then(response => {
          testRuleResponse = response
          return testRuleResponse
        })
        .catch(error => {
          testRuleResponse = {}
          return Promise.reject(error)
        })
    })
    .catch(error => {
      return Promise.reject(error)
    })
}

/**
 * testTransformationJSRule - test transformation rule
 * @param {object} data
 * @returns {Promise}
 */
export function testTransformationJSRule(data, accountId, serviceURL) {
  return appDataService
    .getMPXUserInfo()
    .then(userInfo => {
      const token = userInfo.token
      let queryObject = {}
      queryObject["token"] = token
      queryObject["serviceURL"] = serviceURL
      queryObject["account"] = accountId
      let testRuleURL = utils.getServerUrl() + API_TEST_TRANSFORMATION_JS_RULE
      testRuleURL += "?" + queryString.stringify(queryObject)
      let testRuleResponse = {}
      return axios
        .post(testRuleURL, data)
        .then(response => {
          testRuleResponse = response
          return testRuleResponse
        })
        .catch(error => {
          testRuleResponse = {}
          return Promise.reject(error)
        })
    })
    .catch(error => {
      return Promise.reject(error)
    })
}

/**
 * testTransformationRuleSet - test validation rule
 * @param {object} data
 * @returns {Promise}
 */
export function testTransformationRuleSet(data, accountId, serviceURL) {
  return appDataService
    .getMPXUserInfo()
    .then(userInfo => {
      const token = userInfo.token
      let queryObject = {}
      queryObject["token"] = token
      queryObject["serviceURL"] = serviceURL
      queryObject["account"] = accountId
      let testRuleURL = utils.getServerUrl() + API_TEST_TRANSFORMATION_RULE_SET
      testRuleURL += "?" + queryString.stringify(queryObject)
      let testRuleResponse = {}
      return axios
        .post(testRuleURL, data)
        .then(response => {
          testRuleResponse = response
          return testRuleResponse
        })
        .catch(error => {
          testRuleResponse = {}
          return Promise.reject(error)
        })
    })
    .catch(error => {
      return Promise.reject(error)
    })
}

/**
 * testTransformationRuleCollection - test validation rule
 * @param {object} data
 * @returns {Promise}
 */
export function testTransformationRuleCollection(data, accountId, serviceURL) {
  return appDataService
    .getMPXUserInfo()
    .then(userInfo => {
      const token = userInfo.token
      let queryObject = {}
      queryObject["token"] = token
      queryObject["serviceURL"] = serviceURL
      queryObject["account"] = accountId
      let testRuleURL =
        utils.getServerUrl() + API_TEST_TRANSFORMATION_RULE_COLLECTION
      testRuleURL += "?" + queryString.stringify(queryObject)
      let testRuleResponse = {}
      return axios
        .post(testRuleURL, data)
        .then(response => {
          testRuleResponse = response
          return testRuleResponse
        })
        .catch(error => {
          testRuleResponse = {}
          return Promise.reject(error)
        })
    })
    .catch(error => {
      return Promise.reject(error)
    })
}



// WEBPACK FOOTER //
// ./src/MetadataRules/MetadataRulesDataService.js